import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { Buy, L10N, Listing, Rent } from '@smg-real-estate/hg-rets-schema';
import {
  AgencyAgent,
  getRetryingAxiosInstance,
  ListingTypeInformation,
} from '@smg-real-estate/hg-search-libs';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getAcrHeaders, shouldInjectAcrHeaders } from '@/helpers/get-acr-headers';
import {
  getDisplayingListingImageAttachments,
  getExclusiveBlurryImageStartIndex,
  getExclusiveImageStartIndex,
  getListingDefaultLocale,
  getListingPriceData,
} from '@/helpers/ListingData/ListingData';
import { ApiTypes, logErrorToDatadogCustomMetric } from '@/helpers/LogHelper';
import {
  getListingWithPlatformSpecificMediaURLs,
  parsePlatformSpecificMediaURL,
} from '@/helpers/ParsePlatformSpecificMediaURL/ParsePlatformSpecificMediaURL';
import { useListerBrandingStore } from '@/store/modules/lister-branding';
import { useNewConstructionStore } from '@/store/modules/new-construction';
import type { HgImage } from '@/store/types';
import { ListingItemAndNewConstructionProject } from '@/store/types/listing-state';

export interface ListingIdWithRequestHeadersPayload {
  listingId: string;
  passThroughHeaders?: Record<string, string>;
  authToken?: string;
  languageCode?: HgSupportedLanguage;
}

export interface ListingFetchError {
  message: string;
  listingId: string;
  response?: {
    status: number;
    data: unknown;
    headers: Record<string, string>;
  };
}

const axiosInstance = getRetryingAxiosInstance({
  timeout: 2000,
  retryConfig: {
    retries: 3,
  },
});

export const useListingStore = defineStore('listing', () => {
  // state
  const listing = ref<Listing | undefined>(undefined);
  const listingType = ref<ListingTypeInformation | undefined>(undefined);
  const agencyAgent = ref<AgencyAgent | undefined>(undefined);
  const isLoading = ref(false);
  const dataFetchError = ref<ListingFetchError | undefined>(undefined);

  const hasListing = computed<boolean>(() => !!listing.value);

  const listingDefaultLocale = computed<L10N | null | undefined>(() =>
    listing.value ? getListingDefaultLocale(listing.value) : null,
  );

  // Default-locale title only. PDP visual/meta use `useListingLocalizedContent`; this getter is
  // kept for preview and other consumers that intentionally read the listing primary locale.
  const listingTitle = computed<string | undefined>(() => listingDefaultLocale.value?.text?.title);

  const listingPriceInfo = computed<Buy | Rent | undefined>(() => {
    if (!listing.value) {
      return undefined;
    }
    return getListingPriceData(listing.value);
  });

  const listingImages = computed<HgImage[] | undefined>(() =>
    getDisplayingListingImageAttachments(listing.value),
  );

  const listingOfferType = computed<string | undefined>(() => listing.value?.offerType);

  const exclusiveBlurryImageStartIndex = computed<number>(() =>
    getExclusiveBlurryImageStartIndex(listing.value),
  );

  const exclusiveImageStartIndex = computed<number>(() =>
    getExclusiveImageStartIndex(listing.value),
  );

  // actions
  async function handleFetchSuccess(response: { data: ListingItemAndNewConstructionProject }) {
    const listerBrandingStore = useListerBrandingStore();
    const newConstructionStore = useNewConstructionStore();

    listing.value = getListingWithPlatformSpecificMediaURLs(response.data.listing);
    listingType.value = response.data.listingType;
    dataFetchError.value = undefined;
    agencyAgent.value = response.data.agencyAgent;

    const responseListerBranding = response.data.listerBranding;
    if (responseListerBranding?.logoUrl) {
      responseListerBranding.logoUrl = parsePlatformSpecificMediaURL(
        responseListerBranding.logoUrl,
      );
    }

    if (responseListerBranding) {
      listerBrandingStore.setListerBranding(responseListerBranding);
    } else {
      listerBrandingStore.setListerBranding(undefined);
    }

    await newConstructionStore.handleNewConstructionListings({
      listingId: response.data.listing.id,
      newConstructionData: response.data.listing.newConstructionData,
    });
  }

  function handleFetchError(error: any, listingId: string) {
    const listerBrandingStore = useListerBrandingStore();
    const newConstructionStore = useNewConstructionStore();

    const errorObject: ListingFetchError = { message: error.message, listingId };

    if (error.response) {
      const { status, data, headers } = error.response;
      errorObject.response = { status, data, headers };
    }

    listing.value = undefined;
    listingType.value = undefined;
    dataFetchError.value = errorObject;
    newConstructionStore.setNewConstructionData(undefined);
    listerBrandingStore.setListerBranding(undefined);
  }

  async function fetchListing({
    listingId,
    passThroughHeaders,
    authToken,
    languageCode,
  }: ListingIdWithRequestHeadersPayload) {
    try {
      isLoading.value = true;
      const listingApiUrlForListingId = `${import.meta.env.VUE_APP_LISTING_API_ENDPOINT}/listing/${listingId}?sanitize=true`;

      const requestHeaders = await prepareRequestHeaders(
        passThroughHeaders,
        authToken,
        languageCode,
      );

      const response = await axiosInstance.get<ListingItemAndNewConstructionProject>(
        listingApiUrlForListingId,
        {
          headers: requestHeaders,
        },
      );

      await handleFetchSuccess(response);
    } catch (error: any) {
      logErrorToDatadogCustomMetric(ApiTypes.listing_api, { listingId, passThroughHeaders }, error);
      handleFetchError(error, listingId);
    } finally {
      isLoading.value = false;
    }
  }

  return {
    // state
    listing,
    listingType,
    agencyAgent,
    isLoading,
    dataFetchError,

    // getters
    hasListing,
    listingDefaultLocale,
    listingTitle,
    listingPriceInfo,
    listingImages,
    listingOfferType,
    exclusiveBlurryImageStartIndex,
    exclusiveImageStartIndex,

    // actions
    fetchListing,
  };
});

/**
 * Prepares and returns request headers for listing API calls
 */
async function prepareRequestHeaders(
  passThroughHeaders?: Record<string, string>,
  authToken?: string,
  languageCode?: string,
): Promise<Record<string, string>> {
  let headers = passThroughHeaders ?? {};

  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  // only add acrHeaders on client side and in production
  if (shouldInjectAcrHeaders()) {
    headers = { ...headers, ...(await getAcrHeaders()) };
  }

  if (languageCode) {
    headers['Accept-Language'] = languageCode;
  }

  return headers;
}
